import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { base } from '../api-config';
import { LIMIT, SKIP } from '../constants';

import { LanguagePreference, LanguagePreferenceRequest } from '../models/preference';
import { LocationPreference, LocationPreferenceRequest } from '../models/preference';
import { ExclusionPreference, ExclusionPreferenceRequest } from '../models/preference';
import { FieldPreference, FieldPreferenceRequest } from '../models/preference';

const endpoint = `${base}/preferences`;

@Injectable({
  providedIn: 'root'
})
export class PreferenceService {

  constructor(private http: HttpClient) {}

  private getParams(surveyId: number, userId: number, skip: number, limit: number) {
    return new HttpParams()
      .append('survey_id', surveyId)
      .append('user_id', userId)
      .append('skip', skip)
      .append('limit', limit);
  }

  getLangPrefs(surveyId: number, userId: number, skip: number = SKIP, limit: number = LIMIT): Observable<LanguagePreference[]> {
    const params = this.getParams(surveyId, userId, skip, limit);
    return this.http.get<LanguagePreference[]>(`${endpoint}/language/`, { params });
  }

  createLangPref(pref: LanguagePreferenceRequest): Observable<LanguagePreference> {
    return this.http.post<LanguagePreference>(`${endpoint}/language/`, pref);
  }

  updateLangPref(id: number, pref: LanguagePreferenceRequest): Observable<LanguagePreference> {
    return this.http.put<LanguagePreference>(`${endpoint}/language/${id}`, pref);
  }

  deleteLangPref(id: number): Observable<string> {
    return this.http.delete<string>(`${endpoint}/language/${id}`);
  }

  getLocPrefs(surveyId: number, userId: number, skip: number = SKIP, limit: number = LIMIT): Observable<LocationPreference[]> {
    const params = this.getParams(surveyId, userId, skip, limit);
    return this.http.get<LocationPreference[]>(`${endpoint}/location/`, { params });
  }

  createLocPref(pref: LocationPreferenceRequest): Observable<LocationPreference> {
    return this.http.post<LocationPreference>(`${endpoint}/location/`, pref);
  }

  updateLocPref(id: number, pref: LocationPreferenceRequest): Observable<LocationPreference> {
    return this.http.put<LocationPreference>(`${endpoint}/location/${id}`, pref);
  }

  deleteLocPref(id: number): Observable<string> {
    return this.http.delete<string>(`${endpoint}/location/${id}`);
  }

  getExclusionPreferences(surveyId: number, userId: number, skip: number = SKIP, limit: number = LIMIT): Observable<ExclusionPreference[]> {
    const params = this.getParams(surveyId, userId, skip, limit);
    return this.http.get<ExclusionPreference[]>(`${endpoint}/exclusion/`, { params });
  }

  createExclusionPreference(pref: ExclusionPreferenceRequest): Observable<ExclusionPreference> {
    return this.http.post<ExclusionPreference>(`${endpoint}/exclusion/`, pref);
  }

  updateExclPref(id: number, pref: ExclusionPreferenceRequest): Observable<ExclusionPreference> {
    return this.http.put<ExclusionPreference>(`${endpoint}/exclusion/${id}`, pref);
  }

  deleteExclusionPreference(id: number): Observable<string> {
    return this.http.delete<string>(`${endpoint}/exclusion/${id}`);
  }

  getFieldPrefs(surveyId: number, userId: number, skip: number = SKIP, limit: number = LIMIT): Observable<FieldPreference[]> {
    const params = this.getParams(surveyId, userId, skip, limit);
    return this.http.get<FieldPreference[]>(`${endpoint}/field/`, { params });
  }

  createFieldPref(pref: FieldPreferenceRequest): Observable<FieldPreference> {
    return this.http.post<FieldPreference>(`${endpoint}/field/`, pref);
  }

  updateFieldPref(id: number, pref: FieldPreferenceRequest): Observable<FieldPreference> {
    return this.http.put<FieldPreference>(`${endpoint}/field/${id}`, pref);
  }

  deleteFieldPref(id: number): Observable<string> {
    return this.http.delete<string>(`${endpoint}/field/${id}`);
  }
}
